import type {
  WordPressAssetStatus,
  WordPressPublicationItem,
  WordPressSyncStatus,
} from './types';

export type WordPressUiStatus =
  | 'unselected'
  | 'needs_image'
  | 'ready_to_publish'
  | 'changes_pending'
  | 'publishing'
  | 'unpublishing'
  | 'published'
  | 'published_with_warnings'
  | 'unpublished'
  | 'error';

type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

function deriveWordPressUiStatus(
  enabled: boolean,
  hasWordPressImage: boolean,
  syncStatus: WordPressSyncStatus | string | undefined,
  hasPendingChanges: boolean,
  wordpressStatus?: string,
  wordpressAction?: string,
  hasActiveSyncWarnings = false,
): WordPressUiStatus {
  if (syncStatus === 'sync_error') return 'error';
  if (wordpressAction === 'unpublish' && syncStatus === 'sync_pending') return 'unpublishing';
  if (syncStatus === 'sync_pending' && !(wordpressStatus === 'draft' && (!wordpressAction || wordpressAction === 'none'))) return 'publishing';
  if (wordpressStatus === 'draft' && wordpressAction !== 'unpublish') return 'unpublished';
  if (!enabled) return 'unselected';
  if (hasPendingChanges) return 'changes_pending';
  if (syncStatus === 'synced') return hasActiveSyncWarnings ? 'published_with_warnings' : 'published';
  if (!hasWordPressImage) return 'needs_image';
  return 'ready_to_publish';
}

export function getWordPressUiStatusFromItem(item: WordPressPublicationItem | null | undefined): WordPressUiStatus {
  return deriveWordPressUiStatus(
    !!item?.publication?.enabled,
    !!item?.publication?.rendition,
    item?.publication?.syncStatus,
    !!item?.publication?.hasPendingChanges,
    item?.publication?.wordpressStatus,
    item?.publication?.wordpressAction,
    !!item?.publication?.hasActiveSyncWarnings,
  );
}

export function getWordPressUiStatusFromAssetStatus(status: WordPressAssetStatus | null | undefined): WordPressUiStatus {
  const primaryPublication = status?.publications?.find(publication => publication.target === status.target)
    || status?.publications?.[0];
  return deriveWordPressUiStatus(
    !!status?.enabled,
    !!status?.hasWordPressImage,
    status?.syncStatus,
    !!status?.hasPendingChanges,
    status?.wordpressStatus,
    status?.wordpressAction,
    !!primaryPublication?.hasActiveSyncWarnings,
  );
}

export function getWordPressUiStatusLabel(status: WordPressUiStatus): string {
  switch (status) {
    case 'unselected':
      return 'Ej vald';
    case 'needs_image':
      return 'Behöver WP-bild';
    case 'ready_to_publish':
      return 'Klar att publicera';
    case 'changes_pending':
      return 'Ändringar väntar';
    case 'publishing':
      return 'Publiceras';
    case 'unpublishing':
      return 'Avpubliceras';
    case 'published':
      return 'Publicerad';
    case 'published_with_warnings':
      return 'Publicerad med varningar';
    case 'unpublished':
      return 'Avpublicerad';
    case 'error':
      return 'Fel';
    default:
      return status;
  }
}

export function getWordPressUiStatusDescription(status: WordPressUiStatus): string {
  switch (status) {
    case 'unselected':
      return 'Den här asseten används inte i WordPress ännu.';
    case 'needs_image':
      return 'Skapa en WP-bild för att kunna publicera.';
    case 'ready_to_publish':
      return 'WP-bilden är klar. Publicera när du är nöjd.';
    case 'changes_pending':
      return 'Asseten har ändrats efter senaste WordPress-sync. Ompublicera ändringarna.';
    case 'publishing':
      return 'Publicering pågår eller väntar på bekräftelse från WordPress.';
    case 'unpublishing':
      return 'Avpublicering väntar på bekräftelse från WordPress. Posten sätts till utkast och raderas inte.';
    case 'published':
      return 'Den senaste WP-bilden är publicerad.';
    case 'published_with_warnings':
      return 'Posten är publicerad, men WordPress rapporterade värden som saknas där. Se synkvarningarna nedan.';
    case 'unpublished':
      return 'Den länkade WordPress-posten är satt till utkast. Den kan publiceras igen senare.';
    case 'error':
      return 'Publiceringen misslyckades. Försök igen.';
    default:
      return '';
  }
}

export function getWordPressUiStatusColor(status: WordPressUiStatus): ChipColor {
  switch (status) {
    case 'needs_image':
      return 'warning';
    case 'ready_to_publish':
      return 'info';
    case 'changes_pending':
      return 'warning';
    case 'publishing':
    case 'unpublishing':
      return 'info';
    case 'published':
      return 'success';
    case 'published_with_warnings':
      return 'warning';
    case 'unpublished':
      return 'default';
    case 'error':
      return 'error';
    case 'unselected':
    default:
      return 'default';
  }
}
